"use client";

import { useRef } from "react";
import gsap from "gsap";
import { useGSAP } from "@gsap/react";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { UserCheck, Coins, Zap, ShieldCheck, Sparkles, ChartLine as LineChart, Check, X, ArrowRight } from 'lucide-react';
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
gsap.registerPlugin(useGSAP, ScrollTrigger);
const keyBenefits = [{
  iconName: "UserCheck",
  title: "1-on-1 Direct Senior Partner",
  subtitle: "No Juniors or Account Managers",
  description: "When you hire me, you work directly with me. Your campaign is never delegated to junior trainees or outsourced account handlers who lack strategic depth.",
  blushHighlight: "100% Direct Expert Access"
}, {
  iconName: "Coins",
  title: "Transparent & Cost Efficient",
  subtitle: "Zero Agency Overhead",
  description: "You pay strictly for senior-level SEO execution and strategic insight—not fancy agency offices, sales commission structures, or unnecessary administrative bloat.",
  blushHighlight: "Higher ROI on Budget"
}, {
  iconName: "Zap",
  title: "Agile & Tailored Strategy",
  subtitle: "No Cookie-Cutter Templates",
  description: "I build custom SEO roadmaps aligned with your precise revenue goals. My recommendations adapt quickly without waiting weeks for corporate agency sign-offs.",
  blushHighlight: "Custom SEO Roadmaps"
}, {
  iconName: "ShieldCheck",
  title: "Complete Accountability",
  subtitle: "Direct & Honest Measurement",
  description: "I take personal pride in every result delivered. You receive honest, unfiltered performance reporting with clear explanations of exact metrics and business impact.",
  blushHighlight: "Clear Monthly Deliverables"
}, {
  iconName: "Sparkles",
  title: "Modern AI & Technical Mastery",
  subtitle: "Future-Proof Search Growth",
  description: "I actively optimize your digital presence for both traditional Google search rankings and modern AI-powered search engines, staying ahead of algorithm shifts.",
  blushHighlight: "AI-Ready Search Visibility"
}, {
  iconName: "LineChart",
  title: "Hands-On Implementation",
  subtitle: "Practical Solutions, Not Just Audits",
  description: "I do not just send long technical PDFs and leave you to figure it out. I work directly alongside your internal developers or execute fixes directly myself.",
  blushHighlight: "End-to-End Execution"
}];
const comparisonRows = [{
  feature: "Primary Contact",
  consultant: "Yassine Essaadi (Direct Senior SEO Expert)",
  agency: "Junior Account Manager / Rotating Representative"
}, {
  feature: "Strategy Ownership",
  consultant: "Bespoke strategy created specifically for your brand",
  agency: "Standardized agency templates & rigid packages"
}, {
  feature: "Cost Structure",
  consultant: "Efficient spend focused directly on active SEO execution",
  agency: "High overhead fees funding agency management & sales"
}, {
  feature: "Agility & Communication",
  consultant: "Fast response time & immediate strategic pivoting",
  agency: "Slow turnarounds hindered by internal bureaucracy"
}, {
  feature: "Reporting & Transparency",
  consultant: "Honest, metric-driven reports showing true revenue impact",
  agency: "Automated superficial PDFs with vanity metrics"
}];
export default function ConsultantBenefits() {
  const containerRef = useRef<HTMLDivElement>(null);
  useGSAP(() => {
    const el = containerRef.current;
    if (!el) return;
    const mm = gsap.matchMedia();
    mm.add("(prefers-reduced-motion: no-preference)", () => {
      gsap.set(".benefit-card", {
        y: 30
      });
      gsap.to(".benefit-card", {
        opacity: 1,
        y: 0,
        duration: 0.6,
        stagger: 0.1,
        ease: "power2.out",
        scrollTrigger: {
          trigger: el,
          start: "top 80%",
          once: true
        }
      });
      gsap.set(".comparison-box", {
        y: 35
      });
      gsap.to(".comparison-box", {
        opacity: 1,
        y: 0,
        duration: 0.7,
        ease: "power2.out",
        scrollTrigger: {
          trigger: ".comparison-box",
          start: "top 85%",
          once: true
        }
      });
    });
    mm.add("(prefers-reduced-motion: reduce)", () => {
      gsap.set(".benefit-card", {
        opacity: 1,
        y: 0
      });
      gsap.set(".comparison-box", {
        opacity: 1,
        y: 0
      });
    });
    const refresh = () => ScrollTrigger.refresh();
    requestAnimationFrame(refresh);
    if (document.readyState === "complete") refresh();else window.addEventListener("load", refresh, {
      once: true
    });
    document.fonts?.ready.then(refresh);
  }, {
    scope: containerRef
  });
  const getIcon = (iconName: string) => {
    switch (iconName) {
      case "UserCheck":
        return <UserCheck className="size-6 text-primary" />;
      case "Coins":
        return <Coins className="size-6 text-primary" />;
      case "Zap":
        return <Zap className="size-6 text-primary" />;
      case "ShieldCheck":
        return <ShieldCheck className="size-6 text-primary" />;
      case "Sparkles":
        return <Sparkles className="size-6 text-primary" />;
      case "LineChart":
        return <LineChart className="size-6 text-primary" />;
      default:
        return <UserCheck className="size-6 text-primary" />;
    }
  };
  return <section id="consultantbenefits" data-nav="light" className="bg-background text-foreground py-20 md:py-28 px-4 sm:px-6 lg:px-8 border-b border-border">
      <div ref={containerRef} className="max-w-7xl mx-auto space-y-16 md:space-y-24">
        
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto space-y-4">
          <Badge variant="secondary" className="bg-secondary text-secondary-foreground font-medium px-3 py-1 rounded-full text-xs uppercase tracking-wider border border-accent/20">
            Independent Advantage
          </Badge>
          <h2 className="font-default text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-foreground">
            Why Work Directly With Me vs. an SEO Agency
          </h2>
          <p className="text-base sm:text-lg text-muted-foreground leading-relaxed">
            I offer a high-touch, senior-level consultancy experience designed to deliver sustainable search growth. You work directly with me—the expert actually executing your strategy—without agency markups or junior handoffs.
          </p>
        </div>

        {/* Benefits Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
          {keyBenefits.map((item, i) => <Card key={i} data-index={i} className="benefit-card bg-card border border-border shadow-sm hover:shadow-md transition-shadow rounded-xl flex flex-col justify-between overflow-hidden" style={{
          opacity: 0
        }}>
              <CardHeader className="space-y-3 pb-2">
                <div className="flex items-center justify-between gap-3">
                  <div className="p-3 rounded-lg bg-primary/10 w-fit">
                    {getIcon(item.iconName)}
                  </div>
                  <span className="inline-flex items-center text-xs font-semibold px-2.5 py-1 rounded-full bg-secondary text-foreground border border-accent/30">
                    {item.blushHighlight}
                  </span>
                </div>
                <div>
                  <CardTitle className="font-default text-xl font-bold text-foreground">
                    {item.title}
                  </CardTitle>
                  <CardDescription className="text-sm font-medium text-primary mt-1">
                    {item.subtitle}
                  </CardDescription>
                </div>
              </CardHeader>
              <CardContent className="pt-2">
                <p className="text-sm text-muted-foreground leading-relaxed">
                  {item.description}
                </p>
              </CardContent>
            </Card>)}
        </div>

        {/* Direct Comparison Table */}
        <div className="comparison-box bg-card border border-border rounded-2xl shadow-md overflow-hidden p-6 sm:p-8 md:p-10" style={{
        opacity: 0
      }}>
          <div className="max-w-3xl mb-8 space-y-2">
            <h3 className="font-default text-2xl sm:text-3xl font-bold text-foreground">
              Direct Comparison at a Glance
            </h3>
            <p className="text-muted-foreground text-sm sm:text-base">
              See how my personalized SEO consultancy compares to hiring a traditional agency model.
            </p>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse min-w-[600px]">
              <thead>
                <tr className="border-b border-border text-sm font-semibold text-foreground">
                  <th className="py-4 px-4 w-1/4">Key Aspect</th>
                  <th className="py-4 px-4 w-3/8 bg-secondary/50 rounded-t-lg text-primary font-bold">
                    YE Consultancy (Yassine)
                  </th>
                  <th className="py-4 px-4 w-3/8 text-muted-foreground">
                    Traditional SEO Agency
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border text-sm">
                {comparisonRows.map((row, i) => <tr key={i} data-index={i} className="hover:bg-muted/40 transition-colors">
                    <td className="py-4 px-4 font-semibold text-foreground">
                      {row.feature}
                    </td>
                    <td className="py-4 px-4 bg-secondary/20 font-medium text-foreground">
                      <div className="flex items-start gap-2">
                        <Check className="size-4 text-primary shrink-0 mt-0.5" />
                        <span>{row.consultant}</span>
                      </div>
                    </td>
                    <td className="py-4 px-4 text-muted-foreground">
                      <div className="flex items-start gap-2">
                        <X className="size-4 text-destructive shrink-0 mt-0.5" />
                        <span>{row.agency}</span>
                      </div>
                    </td>
                  </tr>)}
              </tbody>
            </table>
          </div>

          {/* Bottom Callout & Action */}
          <div className="mt-8 pt-6 border-t border-border flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 bg-background p-4 sm:p-6 rounded-xl border">
            <div className="space-y-1">
              <p className="font-semibold text-foreground text-base">
                {"“My promise is direct strategic involvement in every account.”"}
              </p>
              <p className="text-xs text-muted-foreground">
                You never have to wonder who is managing your search engine performance.
              </p>
            </div>
            <Button asChild size="lg" className="shrink-0 font-medium">
              <Link to="/ContactPage">
                <span>Book a Free Consultation</span>
                <ArrowRight className="size-4 ml-1" />
              </Link>
            </Button>
          </div>
        </div>

      </div>
    </section>;
}